// file: `react/src/components/UserSelect/UserSelect.tsx`

import React, { useRef, useEffect, useContext, useState, useCallback, useMemo } from 'react';
import Select, { GroupBase, SelectInstance, StylesConfig } from 'react-select';
import axios from 'axios';
import { UserContext, User } from '../UserContext.tsx';
import { expressURL, getAuthHeaders } from '../../utils/networkUtils';

type Option = { value: string; label: string };

interface UserSelectProps {
    onChange: (selectedOptions: User[]) => void;
    excludeUserIds?: Array<string | number>;
    allowEmailLookup?: boolean;
    onUnknownEmail?: (email: string) => void;
}

const LOOKUP_PREFIX = '__lookup__:';
const EMAIL_LIKE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function looksLikeEmail(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 3 && EMAIL_LIKE.test(trimmed);
}

function useDarkMode() {
  const [isDark, setIsDark] = useState(
    () => document.documentElement.getAttribute('data-bs-theme') === 'dark'
  );
  useEffect(() => {
    const sync = () => {
      setIsDark(document.documentElement.getAttribute('data-bs-theme') === 'dark');
    };
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return isDark;
}

const darkStyles: StylesConfig<Option, true> = {
  control: (base, state) => ({
    ...base,
    backgroundColor: '#2b3035',
    borderColor: state.isFocused ? '#86b7fe' : '#495057',
    color: '#dee2e6',
    boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#212529',
    color: '#dee2e6',
    zIndex: 20,
  }),
  menuList: (base) => ({
    ...base,
    backgroundColor: '#212529',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused || state.isSelected ? '#375a7f' : '#212529',
    color: '#f8f9fa',
    cursor: 'pointer',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#343a40',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#dee2e6',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#adb5bd',
    ':hover': { backgroundColor: '#842029', color: '#fff' },
  }),
  input: (base) => ({
    ...base,
    color: '#dee2e6',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#adb5bd',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#dee2e6',
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: '#495057',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#adb5bd',
  }),
  clearIndicator: (base) => ({
    ...base,
    color: '#adb5bd',
  }),
};

const UserSelect: React.FC<UserSelectProps> = ({
  onChange,
  excludeUserIds,
  allowEmailLookup = true,
  onUnknownEmail
}) => {
  const selectRef = useRef<SelectInstance<Option, true, GroupBase<Option>>>(null);
  const lookupTimerRef = useRef<number | null>(null);
  const lookupInFlightRef = useRef<string | null>(null);
  const { users } = useContext(UserContext) as { users: User[] };
  const isDark = useDarkMode();
  const excluded = new Set((excludeUserIds || []).map((id) => String(id)));

  const [extraUsers, setExtraUsers] = useState<User[]>([]);
  const [selected, setSelected] = useState<Option[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [lookupError, setLookupError] = useState('');

  const allUsers = useMemo(() => {
    const byId = new Map<string, User>();
    for (const user of users) {
      byId.set(String(user.id), user);
    }
    for (const user of extraUsers) {
      byId.set(String(user.id), user);
    }
    return [...byId.values()];
  }, [users, extraUsers]);

  const usersById = useMemo(
    () => new Map(allUsers.map((user) => [String(user.id), user])),
    [allUsers]
  );

  const userOptions = useMemo(
    () =>
      allUsers
        .filter((user) => !excluded.has(String(user.id)))
        .map((user) => ({
          value: String(user.id),
          label: user.fullName || user.shortName,
        })),
    [allUsers, excluded]
  );

  const notifyChange = useCallback(
    (nextSelected: Option[]) => {
      const selectedUsers = nextSelected
        .map((option) => usersById.get(option.value))
        .filter((user): user is User => !!user);
      onChange(selectedUsers);
    },
    [onChange, usersById]
  );

  const addUserToSelection = useCallback(
    (user: User) => {
      setExtraUsers((prev) =>
        prev.some((entry) => String(entry.id) === String(user.id)) ? prev : [...prev, user]
      );

      const option = {
        value: String(user.id),
        label: user.fullName || user.shortName,
      };

      setSelected((prev) => {
        if (prev.some((entry) => entry.value === option.value)) {
          return prev;
        }
        const next = [...prev, option];
        const known = new Map(usersById);
        known.set(String(user.id), user);
        onChange(next.map((entry) => known.get(entry.value)).filter((item): item is User => !!item));
        return next;
      });
      setInputValue('');
      setLookupError('');
    },
    [onChange, usersById]
  );

  const lookupUserByEmail = useCallback(
    async (email: string) => {
      if (!allowEmailLookup) return;
      const trimmed = email.trim();
      if (!looksLikeEmail(trimmed)) {
        return;
      }

      const normalized = trimmed.toLowerCase();
      if (lookupInFlightRef.current === normalized) {
        return;
      }
      lookupInFlightRef.current = normalized;
      setLookupError('');

      try {
        const response = await axios.get(
          `${expressURL()}/api/lookup-user?email=${encodeURIComponent(trimmed)}`,
          getAuthHeaders()
        );
        addUserToSelection(response.data as User);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          if (onUnknownEmail) {
            onUnknownEmail(trimmed);
            setInputValue('');
            setLookupError('');
          } else {
            setLookupError('That email is not an active account.');
          }
        } else {
          console.error('UserSelect: lookup failed', error);
        }
      } finally {
        if (lookupInFlightRef.current === normalized) {
          lookupInFlightRef.current = null;
        }
      }
    },
    [addUserToSelection, allowEmailLookup, onUnknownEmail]
  );

  const menuOptions = useMemo(() => {
    const trimmed = inputValue.trim();
    if (!allowEmailLookup || !looksLikeEmail(trimmed)) {
      return userOptions;
    }

    return [
      ...userOptions,
      {
        value: `${LOOKUP_PREFIX}${trimmed}`,
        label: `Look up ${trimmed}`,
      },
    ];
  }, [inputValue, userOptions, allowEmailLookup]);

  useEffect(() => {
    selectRef.current?.focus();
  }, []);

  useEffect(() => {
    if (lookupTimerRef.current != null) {
      window.clearTimeout(lookupTimerRef.current);
    }

    const trimmed = inputValue.trim();
    if (!allowEmailLookup || !looksLikeEmail(trimmed)) {
      return undefined;
    }

    lookupTimerRef.current = window.setTimeout(() => {
      lookupUserByEmail(trimmed);
    }, 600);

    return () => {
      if (lookupTimerRef.current != null) {
        window.clearTimeout(lookupTimerRef.current);
      }
    };
  }, [inputValue, lookupUserByEmail, allowEmailLookup]);

  return (
    <div>
      <Select
        isMulti
        inputValue={inputValue}
        onInputChange={(value, meta) => {
          if (meta.action === 'input-change') {
            setInputValue(value);
            if (lookupError) {
              setLookupError('');
            }
          }
        }}
        options={menuOptions}
        value={selected}
        onChange={(newValue) => {
          const options = [...newValue];
          const lookupOption = options.find((option) => option.value.startsWith(LOOKUP_PREFIX));
          if (lookupOption) {
            const email = lookupOption.value.slice(LOOKUP_PREFIX.length);
            lookupUserByEmail(email);
            return;
          }

          setSelected(options);
          notifyChange(options);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && looksLikeEmail(inputValue)) {
            event.preventDefault();
            lookupUserByEmail(inputValue);
          }
        }}
        placeholder={allowEmailLookup ? 'Type a name or email' : 'Type a name from the school roster'}
        ref={selectRef}
        classNamePrefix="user-select"
        styles={isDark ? darkStyles : undefined}
        theme={(theme) => (isDark ? {
          ...theme,
          colors: {
            ...theme.colors,
            primary: '#0d6efd',
            primary25: '#375a7f',
            primary50: '#0a58ca',
            neutral0: '#212529',
            neutral5: '#2b3035',
            neutral10: '#343a40',
            neutral20: '#495057',
            neutral30: '#6c757d',
            neutral40: '#adb5bd',
            neutral50: '#adb5bd',
            neutral60: '#ced4da',
            neutral80: '#dee2e6',
            neutral90: '#f8f9fa',
          },
        } : theme)}
      />
      {lookupError && (
        <p className="small mt-1 mb-0 text-danger">{lookupError}</p>
      )}
    </div>
  );
};

export default UserSelect;
